import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plane } from 'lucide-react';
import { toast } from 'sonner';
import { useAvailableLoads, useMyManifestStatus, useEligibility } from '@/hooks/useFunJumper';
import EligibilityPanel from '@/components/funJumper/EligibilityPanel';
import EligibilityBlocker from '@/components/funJumper/EligibilityBlocker';
import LoadCard from '@/components/funJumper/LoadCard';
import MyStatusPanel from '@/components/funJumper/MyStatusPanel';
import SelfManifestDialog from '@/components/funJumper/SelfManifestDialog';

const FunJumperPage = () => {
  const { t } = useTranslation();

  const { data: eligibility, isLoading: eligLoading } = useEligibility();
  const { data: loads = [], isLoading: loadsLoading } = useAvailableLoads();
  const { data: myStatus = [], isLoading: statusLoading } = useMyManifestStatus();

  const [selectedLoad, setSelectedLoad] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (!myStatus || myStatus.length === 0) {
      prevStatusRef.current = myStatus;
      return;
    }

    const prev = prevStatusRef.current;
    if (prev && prev.length > 0) {
      for (const entry of myStatus) {
        if (entry.me?.kind === 'manifested') {
          const prevEntry = prev.find(p => p.loadId === entry.loadId);
          if (prevEntry && prevEntry.me?.kind === 'waitlisted') {
            toast.success(t('funJumper.promoted', { loadNumber: entry.loadNumber }));
          }
        }
      }
    }

    prevStatusRef.current = myStatus;
  }, [myStatus, t]);

  const handleManifest = (load) => {
    setSelectedLoad(load);
    setDialogOpen(true);
  };

  const eligible = eligibility?.eligible;
  const isLoading = eligLoading || loadsLoading;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Plane className="h-6 w-6" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('funJumper.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('funJumper.subtitle')}</p>
        </div>
      </div>

      <EligibilityPanel eligibility={eligibility} />

      <div className="relative">
        {eligible === false && eligibility?.reasons && (
          <EligibilityBlocker reasons={eligibility.reasons} />
        )}

        <div className={eligible === false ? 'opacity-30 pointer-events-none select-none' : ''}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <h2 className="text-lg font-semibold">{t('funJumper.availableLoads')}</h2>
                {loads.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground rounded-lg border border-dashed">
                    {t('funJumper.noLoads')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {loads.map((load) => (
                      <LoadCard
                        key={load.loadId}
                        load={load}
                        myStatus={myStatus}
                        onManifest={handleManifest}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <MyStatusPanel statusData={myStatus} isLoading={statusLoading} />
              </div>
            </div>
          )}
        </div>
      </div>

      <SelfManifestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        load={selectedLoad}
        profile={eligibility?.profile}
      />
    </div>
  );
};

export default FunJumperPage;
